import { useCallback, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature } from 'topojson';
import { geoPath } from 'd3-geo';
import { assertNotNil } from '../../../utils/types';
import {
  GEO_MAP_DATA_KEY_ATTRIBUTE,
  GEO_MAP_GROUP_CLASSNAME,
  GEO_MAP_HEIGHT,
  GEO_MAP_PATH_CONTAINER,
  GEO_MAP_TOOLTIP_PREFIX,
  getKeyInGeoMapPropertiesByType,
  getMapContainerWidth,
} from './utils';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import { Selection } from 'd3';
import {
  ICountyMapProperties,
  IStateMapProperties,
} from '../../../redux/slices/bootstrap';
import { Geo } from '../../../utils/apollo/graphqlSchema';
import { theme } from '../../../styles/theme';
import { useChartContext } from '../ChartContext';

interface IProps {
  containerId: string;
}

/**
 * useGeoMap is a custom hook that is used to initialize the geo map, and generate the frame of the map.
 * this initial map will be only called once when the component is first rendered.
 */
const useGeoMap = ({ containerId }: IProps) => {
  const svgRef = useRef<Selection<SVGSVGElement, unknown, HTMLElement, any>>();
  const [isGeoMapReady, setIsGeoMapReady] = useState(false);
  const {
    metadata: { geo },
  } = useChartContext();
  assertNotNil(geo);
  const mapData = useBootstrapSelector('geoMapData');
  assertNotNil(mapData);
  const generateCountyGeoMap = useCallback(() => {
    if (svgRef?.current) {
      const rootSvg = svgRef.current;
      const countyTopoData = mapData.countyMapData;
      const stateTopoData = mapData.stateMapData;

      const width = getMapContainerWidth(containerId);
      // setup size of canvas
      rootSvg.attr('width', width).attr('height', GEO_MAP_HEIGHT);
      // add a container to control the default behavior on canvas to disable double click to zoomin
      const groupsContainer = rootSvg
        .append('g')
        .attr('class', GEO_MAP_PATH_CONTAINER)
        .on('dblclick', (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        });

      const countyMapsContainer = groupsContainer.append('g');

      // setup us map with county data
      const projection = d3
        .geoAlbersUsa()
        .fitSize(
          [width, GEO_MAP_HEIGHT],
          feature(countyTopoData, countyTopoData.objects['us-county-map']),
        );
      const mapPath = geoPath().projection(projection);

      const stateFeatures = feature(
        stateTopoData,
        stateTopoData.objects['us-state-map'],
      ).features;

      const propertyKey = getKeyInGeoMapPropertiesByType(geo) as keyof (
        | ICountyMapProperties
        | IStateMapProperties
      );

      // Group features based on the geo type， if geo is DMA, we group by county, otherwise, we group by state
      const groupedFeatures = d3.group(
        geo === Geo.UsDma
          ? feature(countyTopoData, countyTopoData.objects['us-county-map'])
              .features
          : stateFeatures,
        (f: { properties: ICountyMapProperties | IStateMapProperties }) => {
          return f.properties[propertyKey];
        },
      );

      groupedFeatures.forEach((currFeatures) => {
        countyMapsContainer
          .append('g')
          .attr('class', GEO_MAP_GROUP_CLASSNAME)
          .selectAll('path')
          .data(currFeatures)
          .enter()
          .append('path')
          .attr('d', mapPath)
          .attr(GEO_MAP_DATA_KEY_ATTRIBUTE, (d) => d.properties[propertyKey])
          .attr('stroke', 'black')
          .attr('stroke-width', geo === Geo.UsDma ? 0.1 : 0.5);
      });

      // Append state borders
      groupsContainer
        .append('g')
        .selectAll('path')
        // same reason as above, missing types from topojson
        .data(stateFeatures)
        .enter()
        .append('g')
        .append('path')
        .attr('d', mapPath)
        .attr('fill', 'none')
        .attr('stroke', 'black')
        .attr('stroke-width', 0.3);

      // Create tooltip div
      d3.select('#' + containerId)
        .append('div')
        .attr('class', `${GEO_MAP_TOOLTIP_PREFIX}${containerId}`)
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('background-color', theme.colors.white)
        .style('pointer-events', 'none');
    }
  }, [geo, mapData, containerId]);

  // only will be called when the window size changes or first time render
  const initGeoMap = useCallback(() => {
    setIsGeoMapReady(false);
    // clear the svg
    d3.select('#' + containerId)
      .selectAll('svg')
      .remove();

    svgRef.current = d3.select('#' + containerId).append('svg');

    generateCountyGeoMap();
    setIsGeoMapReady(true);
    // set opacity to 0 to hide the map until color is applied
    svgRef.current.style('opacity', 0);
  }, [containerId, generateCountyGeoMap, setIsGeoMapReady]);

  return { initGeoMap, isGeoMapReady, svgRef };
};

export default useGeoMap;
